import { defineStore } from 'pinia';

export const useHomeSectionsStore = defineStore('ap.homeSections', () => {
  // Nova estrutura da API
  const homeSectionData = ref<HomeSectionData | null>(null);

  // Estrutura antiga (para compatibilidade durante transição)
  const homeSection = ref<HomeSection | null>(null);

  const loading = ref(false);
  const error = ref<string | null>(null);

  // Setter para nova estrutura
  function setHomeSectionData(data: HomeSectionData | null) {
    homeSectionData.value = data;
  }

  // Setter para estrutura antiga (compatibilidade)
  function setHomeSection(data: HomeSection | null) {
    homeSection.value = data;
  }

  // ============================================================================
  // NOVA API - Computed Properties
  // ============================================================================

  // Items ordenados por priority
  const sortedItems = computed(() => {
    if (!homeSectionData.value) return [];

    return [...homeSectionData.value.items].sort(
      (a, b) => a.priority - b.priority,
    );
  });

  // Type guard helper
  function isHomeTabsData(
    data: HomeCategoryData | HomeTabsData,
  ): data is HomeTabsData {
    return 'categories' in data;
  }

  // Filtra items válidos (com jogos ou é placeholder ou é tabs)
  const validItems = computed(() => {
    return sortedItems.value.filter((item) => {
      // PLACEHOLDER não precisa de jogos (pode ser componente especial)
      if (item.content_type === 'PLACEHOLDER') return true;

      // TABS pode ter duas estruturas:
      // 1. type="section" -> data.categories (HomeTabsData)
      // 2. type="category" -> data.games (HomeCategoryData)
      if (item.content_type === 'TABS') {
        // type="section": verifica se tem categories com jogos
        if (item.type === 'section' && isHomeTabsData(item.data)) {
          return item.data.categories && item.data.categories.length > 0;
        }

        // type="category": verifica se tem jogos diretos
        if (item.type === 'category' && 'games' in item.data) {
          return item.data.games && item.data.games.length > 0;
        }

        return false;
      }

      // NUMBER e BASIC precisam ter jogos diretos em data.games
      if ('games' in item.data) {
        return item.data.games && item.data.games.length > 0;
      }

      return false;
    });
  });

  // Agrupa por content_type (útil para debug/analytics)
  const itemsByType = computed(() => {
    return sortedItems.value.reduce(
      (acc, item) => {
        if (!acc[item.content_type]) {
          acc[item.content_type] = [];
        }
        acc[item.content_type].push(item);

        return acc;
      },
      {} as Record<HomeSectionContentType, HomeSectionItem[]>,
    );
  });

  const hasItems = computed(() => validItems.value.length > 0);

  // ============================================================================
  // ANTIGA API - Computed Properties (compatibilidade)
  // ============================================================================

  const categories = computed(() => {
    if (!homeSection.value) return [];

    return homeSection.value.categories.sort(
      (a, b) => a.pivot.priority - b.pivot.priority,
    );
  });

  const hasCategories = computed(() => categories.value.length > 0);

  // ============================================================================
  // DETECÇÃO AUTOMÁTICA - Qual estrutura usar
  // ============================================================================

  // Verifica se está usando a nova API
  const isUsingNewApi = computed(() => !!homeSectionData.value);

  // Verifica se a API está funcionando (nova ou antiga)
  const isApiWorking = computed(() => {
    if (isUsingNewApi.value) {
      return !error.value && (hasItems.value || loading.value);
    }

    // Fallback para estrutura antiga
    return !error.value && (hasCategories.value || loading.value);
  });

  return {
    // Nova API
    homeSectionData,
    setHomeSectionData,
    sortedItems,
    validItems,
    itemsByType,
    hasItems,

    // Antiga API (compatibilidade)
    homeSection,
    setHomeSection,
    categories,
    hasCategories,

    // Compartilhado
    loading,
    error,
    isApiWorking,
    isUsingNewApi,
  };
});
